// utils/duplicateForm.js

import { getDraftFormFromFirestore } from './firestore';
import { decompressDataForDraft } from './saveDraftFormToFirestore';
import { safeSetItem, safeGetItem } from './storageUtils';
import {
  sanitizeImageFieldsMetadataMapForPersistence,
  getAssetReferenceId,
} from './assetFieldMetadata';
import {
  sanitizeSubmissionSettingsForDuplicate,
} from '../integrations/formPortability';

/**
 * Duplicates a form from Firestore and sets up a new local form instance.
 *
 * KEY GOALS (mode-aware refactor alignment):
 * - Seed mode-aware keys (e.g. __auto, __published) so builder contexts hydrate immediately.
 * - Seed lastUpdated keys where contexts use cache timestamps.
 * - Keep legacy keys mirrored for compatibility during migration (safe and non-breaking).
 *
 * @param {string} sourceFormId - The Firestore ID of the form to duplicate.
 * @param {{ currentUser: object, organizationId: string }} context
 * @param {Function} initializeNewFormLocally - Function to create new formId and store basic metadata.
 * @returns {Promise<{ status: 'success' | 'error', newFormId?: string, message?: string }>}
 */
export const duplicateForm = async (sourceFormId, context, initializeNewFormLocally) => {
  console.log('[duplicateForm] Starting duplication process...');
  try {
    const { currentUser, organizationId } = context;

    // 1️⃣ Basic validation checks
    if (!sourceFormId) {
      console.error('[duplicateForm] ❌ sourceFormId is missing.');
      return { status: 'error', message: 'No source form ID provided for duplication.' };
    }
    if (!currentUser?.uid) {
      console.error('[duplicateForm] ❌ Missing current user context (uid).');
      return { status: 'error', message: 'No current user found; cannot duplicate form.' };
    }
    if (!organizationId) {
      console.error('[duplicateForm] ❌ Missing organizationId context.');
      return { status: 'error', message: 'No organization context found; cannot duplicate form.' };
    }

    console.log('[duplicateForm] 🔍 Fetching original form from Firestore. Form ID:', sourceFormId);
    const originalForm = await getDraftFormFromFirestore(sourceFormId);

    if (!originalForm) {
      console.error('[duplicateForm] ❌ Failed to fetch form from Firestore or it does not exist:', sourceFormId);
      return { status: 'error', message: 'Form not found or failed to load.' };
    }

    // 2️⃣ Create a new local form shell (generates new formId, sets localStorage flags, etc.)
    const newFormId = initializeNewFormLocally(currentUser.uid);
    console.log('[duplicateForm] 🧬 Generated new formId locally:', newFormId);

    // ────────────────────────────────────────────────────────────────
    // Seed builder view mode for the NEW form id.
    // This prevents ambiguity and ensures effectiveViewMode resolves the same
    // way across FormLayoutContext / FieldMetadataContext / MAF / DDC / FSSC.
    // ────────────────────────────────────────────────────────────────
    try {
      sessionStorage.setItem(
        `form_${newFormId}_layoutVersionMode`,
        JSON.stringify({ value: 'auto', timestamp: Date.now() })
      );
      console.log('[duplicateForm]  Seeded layoutVersionMode=auto for new form:', {
        key: `form_${newFormId}_layoutVersionMode`,
        value: 'auto',
      });
    } catch (e) {
      console.warn('[duplicateForm] ⚠️ Failed to seed layoutVersionMode (non-fatal):', e?.message || e);
    }

    // 3️⃣ Copy over the basic form metadata (title & description)
    const titleKey = `form_${newFormId}_title`;
    const descriptionKey = `form_${newFormId}_description`;
    const fallbackTitle = originalForm?.formDetails?.title || `Untitled Form ${newFormId}`;
    const fallbackDescription = originalForm?.formDetails?.description || 'Add a description';

    // Use sessionStorage or safeSetItem – either is fine for these strings.
    sessionStorage.setItem(titleKey, JSON.stringify(fallbackTitle));
    sessionStorage.setItem(descriptionKey, JSON.stringify(fallbackDescription));
    console.log('[duplicateForm] 📝 Stored title and description to sessionStorage:', {
      title: fallbackTitle,
      description: fallbackDescription,
    });

    // ────────────────────────────────────────────────────────────────
    // Mode-aware key plan
    // - Builder opens duplicated drafts in __auto by default.
    // - Viewer route forces published, so we also seed __published to avoid
    //   “missing cache → Firestore → default layout” issues in preview/viewer flows.
    // - We also mirror legacy keys for compatibility during refactor.
    // ────────────────────────────────────────────────────────────────
    const MODES_TO_SEED = ['auto', 'published'];
    const nowIso = new Date().toISOString();

    // 4️⃣ Decompress the known “compressed” sections into storage
    //    so the builder’s contexts can re-hydrate them.
    //
    // IMPORTANT:
    // - We seed mode-aware keys for every context that now prefers them.
    // - We also mirror legacy keys where older code paths still read them.
    //
    // Firestore blob names expected on `originalForm`:
    // - layoutConfig, theme, fieldsMetadata, dropdownOptions, dropdownDependencies, submissionSettings
    //
    // Storage keys expected by contexts:
    // - Layout:                form_{id}_layoutConfig__{mode}
    // - Fields metadata:       form_{id}_fieldsMetadata__{mode}
    // - Dropdown options:      form_{id}_dropdownOptions__{mode} + lastUpdated
    // - Dropdown dependencies: form_{id}_dropdownDependencies__{mode} + lastUpdated
    // - Submission settings:   submissionSettings_{id}__{mode} + lastUpdated
    //
    // Legacy mirror keys (optional but safe):
    // - form_{id}_layoutConfig
    // - form_{id}_fieldsMetadata
    // - form_{id}_dropdownOptions + form_{id}_dropdownLastUpdated
    // - form_{id}_dropdownDependencies + form_{id}_dropdownDependenciesLastUpdated
    // - submissionSettings_{id} + form_{id}_submissionLastUpdated
    const writeToKeys = (keys, value, labelForLog = '') => {
      keys.forEach((k) => {
        safeSetItem(k, value);
        console.log(`[duplicateForm] 💾 Stored "${k}" with safeSetItem.${labelForLog ? ` (${labelForLog})` : ''}`);
      });
    };

    // Helper to decompress once and then write to many keys.
    const decompressAndSeed = async ({
      blobName,
      logKeyForDecompress,
      targetKeys,
      transformValue = null,
    }) => {
      const compressed = originalForm?.[blobName];

      if (!compressed) {
        console.warn(`[duplicateForm] ⚠️ No compressed data for "${blobName}", skipping...`);
        return { ok: false, value: null };
      }

      // Decompress to get a plain JS object (no automatic storage)
      const parsedData = await decompressDataForDraft(compressed, logKeyForDecompress, false); // persist = false

      const finalData =
        typeof transformValue === 'function'
          ? transformValue(parsedData)
          : parsedData;

      // Write to all provided target keys
      writeToKeys(targetKeys, finalData, `seed:${blobName}`);

      return { ok: true, value: finalData };
    };

    // ────────────────────────────────────────────────────────────────
    // 4A) LayoutConfig (mode-aware + legacy mirror + optional hash placeholders)
    // ────────────────────────────────────────────────────────────────
    const layoutTargets = [
      ...MODES_TO_SEED.map((m) => `form_${newFormId}_layoutConfig__${m}`),
      `form_${newFormId}_layoutConfig`, // legacy mirror
    ];

    const layoutResult = await decompressAndSeed({
      blobName: 'layoutConfig',
      logKeyForDecompress: `form_${newFormId}_layoutConfig__auto`,
      targetKeys: layoutTargets,
    });

    // We do NOT attempt to compute hashes here (FormLayoutContext generates hashes as needed).
    // But we can seed null placeholders so “hash not found” logs are less noisy (optional).
    try {
      MODES_TO_SEED.forEach((m) => {
        safeSetItem(`form_${newFormId}_layoutConfig__${m}_hash`, null);
      });
      safeSetItem(`form_${newFormId}_layoutConfig_hash`, null);
      console.log('[duplicateForm] 🧾 Seeded layout hash placeholders (non-authoritative).');
    } catch (e) {
      console.warn('[duplicateForm] ⚠️ Failed to seed layout hash placeholders (non-fatal):', e?.message || e);
    }

    // ────────────────────────────────────────────────────────────────
    // 4B) FieldsMetadata (mode-aware + legacy mirror)
    // ────────────────────────────────────────────────────────────────
    const fieldsMetaTargets = [
      ...MODES_TO_SEED.map((m) => `form_${newFormId}_fieldsMetadata__${m}`),
      `form_${newFormId}_fieldsMetadata`, // legacy mirror
    ];

    const fieldsMetaResult = await decompressAndSeed({
      blobName: 'fieldsMetadata',
      logKeyForDecompress: `form_${newFormId}_fieldsMetadata__auto`,
      targetKeys: fieldsMetaTargets,
      transformValue: (metadata) => {
        const sanitized =
          sanitizeImageFieldsMetadataMapForPersistence(metadata || {});

        try {
          const imageDiagnostics = Object.entries(sanitized || {})
            .filter(([, meta]) => meta?.type === 'image')
            .map(([fieldId, meta]) => ({
              fieldId,
              assetReferenceId: getAssetReferenceId(meta?.assetReference),
              assetType:
                meta?.assetType ||
                meta?.systemAssetType ||
                meta?.assetReference?.assetType ||
                meta?.assetReference?.systemAssetType ||
                null,
              hasThumbnailPath: Boolean(meta?.thumbnail),
              hasThumb400Path: Boolean(meta?.thumb?.urls?.['400']),
              hasOriginalPath: Boolean(meta?.versions?.original?.path),
              hasRuntimeThumbUrl: Boolean(meta?.thumbUrl),
            }));

          console.log('[duplicateForm] 🖼️ Image metadata diagnostics after sanitize:', {
            sourceFormId,
            newFormId,
            imageCount: imageDiagnostics.length,
            imageDiagnostics,
          });
        } catch (diagErr) {
          console.warn(
            '[duplicateForm] ⚠️ Failed image metadata diagnostics (non-fatal):',
            diagErr?.message || diagErr,
          );
        }

        return sanitized;
      },
    });

    // ────────────────────────────────────────────────────────────────
    // 4C) DropdownOptions (mode-aware + lastUpdated + legacy mirror)
    // ────────────────────────────────────────────────────────────────
    const dropdownOptionsTargets = [
      ...MODES_TO_SEED.map((m) => `form_${newFormId}_dropdownOptions__${m}`),
      `form_${newFormId}_dropdownOptions`, // legacy mirror
    ];

    const dropdownOptionsResult = await decompressAndSeed({
      blobName: 'dropdownOptions',
      logKeyForDecompress: `form_${newFormId}_dropdownOptions__auto`,
      targetKeys: dropdownOptionsTargets,
    });

    // lastUpdated keys (mode-aware + legacy)
    try {
      MODES_TO_SEED.forEach((m) => {
        safeSetItem(`form_${newFormId}_dropdownLastUpdated__${m}`, nowIso);
      });
      safeSetItem(`form_${newFormId}_dropdownLastUpdated`, nowIso);
      console.log('[duplicateForm] 🕒 Seeded dropdownLastUpdated keys:', {
        modes: MODES_TO_SEED,
        nowIso,
      });
    } catch (e) {
      console.warn('[duplicateForm] ⚠️ Failed to seed dropdownLastUpdated keys (non-fatal):', e?.message || e);
    }

    // ────────────────────────────────────────────────────────────────
    // 4D) DropdownDependencies (mode-aware + lastUpdated + legacy mirror)
    // ────────────────────────────────────────────────────────────────
    const dropdownDepsTargets = [
      ...MODES_TO_SEED.map((m) => `form_${newFormId}_dropdownDependencies__${m}`),
      `form_${newFormId}_dropdownDependencies`, // legacy mirror
    ];

    const dropdownDepsResult = await decompressAndSeed({
      blobName: 'dropdownDependencies',
      logKeyForDecompress: `form_${newFormId}_dropdownDependencies__auto`,
      targetKeys: dropdownDepsTargets,
    });

    try {
      MODES_TO_SEED.forEach((m) => {
        safeSetItem(`form_${newFormId}_dropdownDependenciesLastUpdated__${m}`, nowIso);
      });
      safeSetItem(`form_${newFormId}_dropdownDependenciesLastUpdated`, nowIso);
      console.log('[duplicateForm] 🕒 Seeded dropdownDependenciesLastUpdated keys:', {
        modes: MODES_TO_SEED,
        nowIso,
      });
    } catch (e) {
      console.warn('[duplicateForm] ⚠️ Failed to seed dropdownDependenciesLastUpdated keys (non-fatal):', e?.message || e);
    }

    // ────────────────────────────────────────────────────────────────
    // 4E) SubmissionSettings (mode-aware + lastUpdated + legacy mirror)
    //
    // ENTERPRISE INTEGRATION PORTABILITY POLICY
    // - Duplicated forms/templates copy form design and normal submit-action
    //   workflow.
    // - Duplicated forms/templates must NOT inherit executable integration
    //   state or provider-owned UI/runtime state.
    // - Provider-specific cleanup is owned by the integration portability
    //   registry, not by duplicateForm.js.
    // - This prevents copied templates from showing provider output columns
    //   such as Bitly short URLs without a matching
    //   forms/{newFormId}/integrationConfigs/{provider} document.
    // ────────────────────────────────────────────────────────────────
    const submissionSettingsTargets = [
      ...MODES_TO_SEED.map((m) => `submissionSettings_${newFormId}__${m}`),
      `submissionSettings_${newFormId}`, // legacy mirror
    ];

    const submissionSettingsResult = await decompressAndSeed({
      blobName: 'submissionSettings',
      logKeyForDecompress: `submissionSettings_${newFormId}__auto`,
      targetKeys: submissionSettingsTargets,
      transformValue: (settings) => {
        const {
          submissionSettings: sanitizedSubmissionSettings,
          diagnostics: integrationPortabilityDiagnostics,
        } = sanitizeSubmissionSettingsForDuplicate(settings || {}, {
          sourceFormId,
          newFormId,
          operation: 'duplicateForm',
        });

        console.log(
          '[duplicateForm] 🔌 Sanitized integration state from duplicated submissionSettings:',
          {
            sourceFormId,
            newFormId,
            policies: integrationPortabilityDiagnostics,
          }
        );

        return sanitizedSubmissionSettings;
      },
    });

    try {
      MODES_TO_SEED.forEach((m) => {
        safeSetItem(`form_${newFormId}_submissionLastUpdated__${m}`, nowIso);
      });
      safeSetItem(`form_${newFormId}_submissionLastUpdated`, nowIso);
      console.log('[duplicateForm] 🕒 Seeded submissionLastUpdated keys:', {
        modes: MODES_TO_SEED,
        nowIso,
      });
    } catch (e) {
      console.warn('[duplicateForm] ⚠️ Failed to seed submissionLastUpdated keys (non-fatal):', e?.message || e);
    }

    // ────────────────────────────────────────────────────────────────
    // 4F) Theme blob (decompress once) + fan-out into mode-aware keys
    // - Your contexts cache individual theme pieces, not the whole theme blob.
    // - FormThemeContext expects keys like:
    //   form_{id}_{piece}__{mode}
    //   plus themeLastUpdated__{mode}
    // - We also mirror legacy keys for compatibility.
    // ────────────────────────────────────────────────────────────────
    const themeTargetsForWholeBlob = [
      ...MODES_TO_SEED.map((m) => `form_${newFormId}_theme__${m}`), // not used directly by your contexts, but harmless
      `form_${newFormId}_theme`, // legacy mirror (harmless)
    ];

    const themeResult = await decompressAndSeed({
      blobName: 'theme',
      logKeyForDecompress: `form_${newFormId}_theme__auto`,
      targetKeys: themeTargetsForWholeBlob,
    });

    // Fan-out theme pieces
    if (themeResult?.ok && themeResult?.value) {
      try {
        const themeData = themeResult.value;

        console.log('[duplicateForm] 🌈 Theme data (from Firestore blob) after decompression:', themeData);

        const selectedTheme = themeData.selectedTheme ?? 'Paper';
        const colors = themeData.colors ?? {};
        const fonts = themeData.fonts ?? {};
        const submitButtonStyle = themeData.submitButtonStyle ?? {};
        const fieldVariant = themeData.fieldVariant ?? 'outlined';

        // Mode-aware + legacy mirror
        MODES_TO_SEED.forEach((m) => {
          safeSetItem(`form_${newFormId}_selectedTheme__${m}`, selectedTheme);
          safeSetItem(`form_${newFormId}_colors__${m}`, colors);
          safeSetItem(`form_${newFormId}_fonts__${m}`, fonts);
          safeSetItem(`form_${newFormId}_submitButtonStyle__${m}`, submitButtonStyle);
          safeSetItem(`form_${newFormId}_fieldVariant__${m}`, fieldVariant);
          safeSetItem(`form_${newFormId}_themeLastUpdated__${m}`, nowIso);
        });

        safeSetItem(`form_${newFormId}_selectedTheme`, selectedTheme);
        safeSetItem(`form_${newFormId}_colors`, colors);
        safeSetItem(`form_${newFormId}_fonts`, fonts);
        safeSetItem(`form_${newFormId}_submitButtonStyle`, submitButtonStyle);
        safeSetItem(`form_${newFormId}_fieldVariant`, fieldVariant);
        safeSetItem(`form_${newFormId}_themeLastUpdated`, nowIso);

        console.log('[duplicateForm] 🌈 Seeded theme fan-out keys (mode-aware + legacy).', {
          modes: MODES_TO_SEED,
          selectedTheme,
          hasColors: !!colors && typeof colors === 'object',
          hasFonts: !!fonts && typeof fonts === 'object',
          hasSubmitButtonStyle: !!submitButtonStyle && typeof submitButtonStyle === 'object',
          fieldVariant,
          themeLastUpdated: nowIso,
        });
      } catch (err) {
        console.error('[duplicateForm] ❌ Error fanning out theme data:', err);
      }
    } else {
      console.warn('[duplicateForm] ⚠️ Theme data was missing or empty after decompression; skipping fan-out.');
    }

    // 6️⃣ Double-check if all required keys are in storage,
    //    log them out for debugging, and bail with an error if any critical are missing.
    //
    // IMPORTANT:
    // - Do NOT check sessionStorage only — safeSetItem may write to localStorage.
    // - Use safeGetItem for validation so we match your actual storage abstraction.
    console.log('[duplicateForm] 🔎 Verifying decompressed data in storage (mode-aware)...');

    // “Critical” keys for immediate builder hydration in auto mode
    const requiredModeAwareKeys = [
      // Builder caches
      `form_${newFormId}_layoutConfig__auto`,
      `form_${newFormId}_fieldsMetadata__auto`,
      `form_${newFormId}_dropdownOptions__auto`,
      `form_${newFormId}_dropdownDependencies__auto`,
      `submissionSettings_${newFormId}__auto`,

      // LastUpdated keys used by contexts
      `form_${newFormId}_dropdownLastUpdated__auto`,
      `form_${newFormId}_dropdownDependenciesLastUpdated__auto`,
      `form_${newFormId}_submissionLastUpdated__auto`,
      `form_${newFormId}_themeLastUpdated__auto`,

      // Theme fan-out
      `form_${newFormId}_selectedTheme__auto`,
      `form_${newFormId}_colors__auto`,
      `form_${newFormId}_fonts__auto`,
      `form_${newFormId}_submitButtonStyle__auto`,
      `form_${newFormId}_fieldVariant__auto`,
    ];

    // Published seeds (optional but recommended for viewer/forced published flows)
    const recommendedPublishedKeys = [
      `form_${newFormId}_layoutConfig__published`,
      `form_${newFormId}_fieldsMetadata__published`,
      `form_${newFormId}_dropdownOptions__published`,
      `form_${newFormId}_dropdownDependencies__published`,
      `submissionSettings_${newFormId}__published`,
      `form_${newFormId}_themeLastUpdated__published`,
      `form_${newFormId}_selectedTheme__published`,
      `form_${newFormId}_colors__published`,
      `form_${newFormId}_fonts__published`,
      `form_${newFormId}_submitButtonStyle__published`,
      `form_${newFormId}_fieldVariant__published`,
    ];

    const allKeysToCheck = requiredModeAwareKeys.concat(recommendedPublishedKeys);

    const missingKeys = [];
    allKeysToCheck.forEach((storageKey) => {
      const value = safeGetItem(storageKey);

      if (value == null) {
        missingKeys.push(storageKey);
        return;
      }

      // Keep your original “is it a simple string” log style
      if (typeof value !== 'object') {
        console.log(
          `[duplicateForm] ⚠️ Key "${storageKey}" is present but might be a simple string or null. Value:`,
          value
        );
      } else {
        console.log(`[duplicateForm] ✅ Key "${storageKey}" found. Parsed value:`, value);
      }
    });

    if (missingKeys.length > 0) {
      console.warn('[duplicateForm] ❌ Some keys are missing or invalid after decompression:', missingKeys);

      // Provide a little more targeted diagnostics for the most common failure:
      // originalForm missing blobs or decompression returning null.
      console.warn('[duplicateForm] 🔍 Seed results summary:', {
        layoutOk: !!layoutResult?.ok,
        fieldsMetaOk: !!fieldsMetaResult?.ok,
        dropdownOptionsOk: !!dropdownOptionsResult?.ok,
        dropdownDepsOk: !!dropdownDepsResult?.ok,
        submissionSettingsOk: !!submissionSettingsResult?.ok,
        themeOk: !!themeResult?.ok,
      });

      return {
        status: 'error',
        message: `Storage write failed for keys: ${missingKeys.join(', ')}`,
      };
    }

    console.log('[duplicateForm] ✅ Duplication complete for new form:', newFormId);
    return {
      status: 'success',
      newFormId,
      message: 'Form duplicated successfully.',
    };
  } catch (error) {
    console.error('[duplicateForm] ❌ Error during duplication:', error);
    return {
      status: 'error',
      message: 'An unexpected error occurred while duplicating the form.',
    };
  }
};
